import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

/** One column of a bn-table. */
export interface BnColumn {
  /** Key into each row object. */
  key: string;
  /** Header text. */
  label: string;
  align?: "left" | "right" | "center";
  /** CSS width, e.g. "1px" (shrink to content) or "40%". */
  width?: string;
  /** Render this column's cells in the monospace font (ids, digests, SQL). */
  mono?: boolean;
}

/** A row is a plain object; a cell value may be a string, number, or a lit template. */
export type BnRow = Record<string, unknown>;

/** Detail of the `row-click` event. */
export interface BnRowClickDetail {
  row: BnRow;
  /** Index into the currently rendered page, not into the whole result set. */
  index: number;
  /** `row[rowKey]` — the record id, for routing straight to a detail page. */
  id: unknown;
}

/** Detail of the `page-change` event. */
export interface BnPageChangeDetail {
  /**
   * The keyset cursor to fetch, straight from the API's `next` field, or null
   * for the first page. Opaque: pass it back as `?cursor=`, never parse it
   * (`06-api-surface.md` §1.5).
   */
  cursor: string | null;
  direction: "next" | "previous";
}

/**
 * bn-table is the DS data table. Set `.columns` and `.rows` as properties; cell
 * values may be lit TemplateResults (a bn-badge, an action button). It renders a
 * mono uppercase header, hairline separators, a loading skeleton, an empty state
 * and — for the audit explorer and the instance list — keyset pagination.
 *
 * **Pagination is keyset, not offset** (`06-api-surface.md` §1.5): the API
 * answers `{items, next}` and `next` is null exactly on the last page, so there
 * is no page count and no total. The table therefore owns only the cursor
 * *stack* — a handful of opaque strings, which is the one piece every list page
 * would otherwise reimplement — and emits `page-change` asking its owner to
 * fetch. It never caches rows: the page you handed it is held once, in `rows`,
 * and is dropped the moment you assign the next one. Call `resetPagination()`
 * whenever the filter set changes, because a cursor is only valid for the query
 * that produced it.
 *
 * Properties:
 * - `columns`, `rows` — property-only (`.columns=`, `.rows=`).
 * - `empty` — text for the no-rows state; the `empty` slot overrides it with a
 *   full bn-empty-state.
 * - `loading` — draws a skeleton when there are no rows yet, marks the table
 *   `aria-busy`, and disables the pager.
 * - `skeletonRows` — skeleton row count (attribute `skeleton-rows`).
 * - `clickable` — rows become focusable and emit `row-click` on click, Enter
 *   and Space. A row is still announced as a row, so where the row click is the
 *   only route to a detail view, also put a link or button in the first cell.
 * - `rowKey` — which field carries the record id (attribute `row-key`,
 *   default `id`).
 * - `caption` — the table's accessible name, rendered visually hidden.
 * - `paginated` — show the pager.
 * - `nextCursor` — the API's `next`; null disables "Next".
 *
 * Slots:
 * - `empty` — replaces the plain `empty` text.
 *
 * Events:
 * - `row-click` — `BnRowClickDetail`. Composed.
 * - `page-change` — `BnPageChangeDetail`. Composed. Fetch it and assign the
 *   result to `.rows` / `.nextCursor`.
 *
 *   <bn-table
 *     .columns=${cols}
 *     .rows=${this.page}
 *     .nextCursor=${this.next}
 *     paginated
 *     clickable
 *     ?loading=${this.loading}
 *     caption="Audit records"
 *     @page-change=${(e: CustomEvent<BnPageChangeDetail>) => this.load(e.detail.cursor)}
 *     @row-click=${(e: CustomEvent<BnRowClickDetail>) => this.open(e.detail.id)}
 *   ></bn-table>
 */
@customElement("bn-table")
export class BnTable extends LitElement {
  @property({ attribute: false }) columns: BnColumn[] = [];
  @property({ attribute: false }) rows: BnRow[] = [];
  /** Text for the no-rows state; the `empty` slot wins over it. */
  @property() empty = "Nothing here yet.";
  /** Draw a skeleton (no rows yet) and mark the table busy. */
  @property({ type: Boolean, reflect: true }) loading = false;
  /** How many skeleton rows to draw while loading. */
  @property({ type: Number, attribute: "skeleton-rows" }) skeletonRows = 5;
  /** Make rows focusable and emit `row-click`. */
  @property({ type: Boolean }) clickable = false;
  /** Field carrying the record id, surfaced in `row-click`. */
  @property({ attribute: "row-key" }) rowKey = "id";
  /** Accessible name for the table; rendered as a visually hidden caption. */
  @property() caption = "";
  /** Render the keyset pager below the table. */
  @property({ type: Boolean }) paginated = false;
  /** The API's `next` cursor. Null means this is the last page. */
  @property({ attribute: false }) nextCursor: string | null = null;

  /** Cursors of the pages behind the current one. Strings only — never rows. */
  @state() private history: (string | null)[] = [];
  /** The cursor that produced the page currently in `rows`. */
  private currentCursor: string | null = null;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
    }
    .scroll {
      overflow-x: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
    }
    caption {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }
    thead th {
      background: var(--bino-surface-subtle);
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-mono);
      font-weight: 600;
      font-size: var(--bino-font-size-xs);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      text-align: left;
      padding: var(--bino-space-sm) calc(var(--bino-space-sm) * 1.5);
      border-bottom: 1px solid var(--bino-border);
      white-space: nowrap;
    }
    tbody td {
      padding: calc(var(--bino-space-sm) * 1.25) calc(var(--bino-space-sm) * 1.5);
      border-bottom: 1px solid var(--bino-border);
      vertical-align: middle;
    }
    tbody tr:last-child td {
      border-bottom: none;
    }
    tbody tr:hover td {
      background: var(--bino-surface-hover);
    }
    tbody tr.clickable {
      cursor: pointer;
    }
    tbody tr.clickable:focus-visible {
      outline: 2px solid var(--bino-focus-ring);
      outline-offset: -2px;
    }
    td.mono {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .empty {
      padding: var(--bino-space-xl);
      text-align: center;
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
    }
    .skeleton {
      display: block;
      height: 0.75em;
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
      animation: pulse 1.4s ease-in-out infinite;
    }
    @keyframes pulse {
      50% {
        opacity: 0.4;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .skeleton {
        animation: none;
      }
    }
    .pager {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--bino-space-md);
      padding: var(--bino-space-sm) calc(var(--bino-space-sm) * 1.5);
      border-top: 1px solid var(--bino-border);
    }
    .page-label {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .page-buttons {
      display: flex;
      gap: var(--bino-space-sm);
    }
    .page-button {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      line-height: 1;
      padding: var(--bino-space-sm) calc(var(--bino-space-sm) * 1.5);
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
      color: var(--bino-text);
      cursor: pointer;
      transition: background var(--bino-transition-fast),
        border-color var(--bino-transition-fast);
    }
    .page-button:hover:not(:disabled) {
      background: var(--bino-surface-hover);
      border-color: var(--bino-border-hover);
    }
    .page-button:focus-visible {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    .page-button:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  `;

  /**
   * Forget the cursor stack and return to page one. Call this when the filter
   * set or sort changes: a cursor is only valid for the query that minted it,
   * and replaying a stale one is a 422 (`06-api-surface.md` §1.5).
   */
  resetPagination() {
    this.history = [];
    this.currentCursor = null;
  }

  private emitPageChange(cursor: string | null, direction: "next" | "previous") {
    this.dispatchEvent(
      new CustomEvent<BnPageChangeDetail>("page-change", {
        detail: { cursor, direction },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onNextPage() {
    if (this.nextCursor === null || this.loading) return;
    this.history = [...this.history, this.currentCursor];
    this.currentCursor = this.nextCursor;
    this.emitPageChange(this.currentCursor, "next");
  }

  private onPreviousPage() {
    if (this.history.length === 0 || this.loading) return;
    const cursor = this.history[this.history.length - 1];
    this.history = this.history.slice(0, -1);
    this.currentCursor = cursor;
    this.emitPageChange(cursor, "previous");
  }

  private onRowClick = (e: Event) => {
    if (!this.clickable) return;
    this.emitRowClick(Number((e.currentTarget as HTMLElement).dataset.index));
  };

  private onRowKeydown = (e: KeyboardEvent) => {
    if (!this.clickable) return;
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault(); // Space would scroll the page.
    this.emitRowClick(Number((e.currentTarget as HTMLElement).dataset.index));
  };

  private emitRowClick(index: number) {
    const row = this.rows[index];
    if (!row) return;
    this.dispatchEvent(
      new CustomEvent<BnRowClickDetail>("row-click", {
        detail: { row, index, id: row[this.rowKey] },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private renderHead() {
    return html`<thead>
      <tr>
        ${this.columns.map(
          (c) =>
            html`<th
              scope="col"
              style=${[c.width ? `width:${c.width}` : "", c.align ? `text-align:${c.align}` : ""]
                .filter(Boolean)
                .join(";")}
            >
              ${c.label}
            </th>`,
        )}
      </tr>
    </thead>`;
  }

  private renderSkeleton() {
    const rows = Array.from({ length: Math.max(1, this.skeletonRows) });
    return rows.map(
      () => html`<tr aria-hidden="true">
        ${this.columns.map(() => html`<td><span class="skeleton"></span></td>`)}
      </tr>`,
    );
  }

  private renderRows() {
    // `this.rows` is iterated in place: no slice, no filter, no internal copy.
    // The audit explorer pages up to 1000 records and the page it handed us is
    // the only copy that exists.
    return this.rows.map(
      (row, index) => html`<tr
        class=${this.clickable ? "clickable" : ""}
        data-index=${index}
        tabindex=${this.clickable ? "0" : nothing}
        @click=${this.onRowClick}
        @keydown=${this.onRowKeydown}
      >
        ${this.columns.map(
          (c) =>
            html`<td class=${c.mono ? "mono" : ""} style=${c.align ? `text-align:${c.align}` : ""}>
              ${row[c.key] as unknown}
            </td>`,
        )}
      </tr>`,
    );
  }

  private renderPager() {
    return html`<nav class="pager" aria-label="Pagination">
      <span class="page-label" aria-live="polite">Page ${this.history.length + 1}</span>
      <span class="page-buttons">
        <button
          class="page-button"
          type="button"
          ?disabled=${this.history.length === 0 || this.loading}
          @click=${this.onPreviousPage}
        >
          Previous
        </button>
        <button
          class="page-button"
          type="button"
          ?disabled=${this.nextCursor === null || this.loading}
          @click=${this.onNextPage}
        >
          Next
        </button>
      </span>
    </nav>`;
  }

  override render() {
    const isEmpty = this.rows.length === 0;
    const body = isEmpty && !this.loading
      ? html`<div class="empty"><slot name="empty">${this.empty}</slot></div>`
      : html`<div class="scroll">
          <table aria-busy=${this.loading ? "true" : "false"}>
            ${this.caption ? html`<caption>${this.caption}</caption>` : nothing}
            ${this.renderHead()}
            <tbody>
              ${isEmpty ? this.renderSkeleton() : this.renderRows()}
            </tbody>
          </table>
        </div>`;
    return html`${body}${this.paginated ? this.renderPager() : nothing}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-table": BnTable;
  }
}
